import React from "react";
import { ArrowUpRight, Play, Sparkles } from "lucide-react";
import { useContact } from "@/contexts/ContactContext";

export const Hero = () => {
  const { openContact } = useContact();
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative w-full overflow-hidden bg-[#000000] pt-40 sm:pt-44 md:pt-48 pb-16 md:pb-20"
    >
      {/* Cinematic backdrop */}
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1580707221190-bd94d9087b7f?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400"
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover opacity-[0.30] animate-kenburns"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/70 to-[#000000]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(146,24,136,0.22),transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,rgba(175,71,170,0.16),transparent_55%)]" />
      </div>

      <div className="relative z-10 max-w-[1440px] mx-auto px-4 sm:px-6 md:px-12 lg:px-16 text-center">
        {/* Overline */}
        <div
          className="hero-enter flex items-center justify-center gap-3 mb-6"
          data-testid="hero-overline"
        >
          <span className="h-px w-10 bg-fuchsia-500" />
          <span className="text-[10px] uppercase tracking-[0.32em] text-fuchsia-400 font-bold">
            Premium Marketing & Growth Partner
          </span>
          <span className="h-px w-10 bg-fuchsia-500" />
        </div>

        {/* Headline */}
        <h1
          data-testid="hero-headline"
          className="hero-enter hero-enter-delay-1 font-display text-[32px] sm:text-5xl md:text-6xl lg:text-[88px] xl:text-[104px] leading-[0.98] tracking-[-0.035em] font-black text-white max-w-[1280px] mx-auto"
        >
          Sponsorships,{" "}
          <span className="italic font-light text-white/60">stories &amp;</span>{" "}
          <span className="text-fuchsia-500">cinematic</span> growth.
        </h1>

        {/* Subheadline */}
        <p
          data-testid="hero-subheadline"
          className="hero-enter hero-enter-delay-2 mt-7 md:mt-8 text-base md:text-lg text-white/70 leading-relaxed font-body max-w-3xl mx-auto px-1"
        >
          1902 Enterprises is a growth-focused creative company turning brands
          into the names people remember — through
          <span className="text-white"> high-impact partnerships</span> and
          <span className="text-white"> video-led storytelling</span>, backed by
          strategy, performance marketing, and operational rigour.
        </p>

        {/* CTAs */}
        <div className="hero-enter hero-enter-delay-3 mt-8 md:mt-10 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center justify-center gap-3 max-w-md sm:max-w-none mx-auto">
          <button
            type="button"
            onClick={() => openContact({ source: "hero-primary" })}
            data-testid="hero-cta-primary"
            className="group inline-flex w-full sm:w-auto items-center justify-center gap-2.5 bg-fuchsia-500 hover:bg-fuchsia-400 text-white text-sm font-semibold px-6 py-3.5 rounded-full transition-all duration-300 ember-glow"
          >
            Start a Conversation
            <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform" />
          </button>
          <a
            href="#services"
            data-testid="hero-cta-secondary"
            className="group inline-flex w-full sm:w-auto items-center justify-center gap-2.5 border border-white/20 hover:border-fuchsia-400 hover:bg-white/5 text-white text-sm font-semibold px-6 py-3.5 rounded-full transition-all duration-300"
          >
            <Play className="w-3.5 h-3.5 fill-white" />
            Explore Our Work
          </a>
        </div>

        {/* Floating tagline */}
        <div
          className="hero-enter hero-enter-delay-4 mt-12 md:mt-14 inline-flex items-center gap-2 text-white/40 text-[10px] tracking-[0.3em] uppercase"
          data-testid="hero-floating-marker"
        >
          <Sparkles className="w-3 h-3 text-fuchsia-400 animate-brand-pulse" />
          Strategy · Story · Scale
          <Sparkles className="w-3 h-3 text-fuchsia-400 animate-brand-pulse" />
        </div>
      </div>
    </section>
  );
};
